(() => {
  'use strict';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const scrollBehavior = () => motion.matches ? 'auto' : 'smooth';
  const previewKeys = ['preview_theme_id', '_fd', 'pb'];
  const preservePreview = (value) => {
    const url = new URL(value, window.location.origin);
    const current = new URL(window.location.href);
    previewKeys.forEach((key) => {
      if (current.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    });
    return url;
  };
  const focusables = (scope) => [...scope.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]')]
    .filter((item) => !item.disabled && item.tabIndex >= 0 && item.getClientRects().length);
  const trapTab = (event, scope) => {
    if (event.key !== 'Tab') return;
    const items = focusables(scope);
    if (!items.length) { event.preventDefault(); scope.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !scope.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !scope.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  };
  const isolateOverlay = (overlay) => {
    const previous = [];
    let branch = overlay;
    while (branch?.parentElement) {
      [...branch.parentElement.children].forEach((sibling) => {
        if (sibling === branch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
        previous.push([sibling, sibling.inert]);
        sibling.inert = true;
      });
      if (branch.parentElement === document.body) break;
      branch = branch.parentElement;
    }
    return () => previous.forEach(([element, wasInert]) => { element.inert = wasInert; });
  };

  function initializeCards(scope = document) {
    const cards = [...scope.querySelectorAll('.af-art-card')];
    if (scope.matches?.('.af-art-card')) cards.unshift(scope);
    cards.forEach((card) => {
      if (card.dataset.afCommerceReady) return;
      card.dataset.afCommerceReady = 'true';
      card.querySelector('[data-af-quick-view]')?.removeAttribute('hidden');
      const gallery = card.querySelector('[data-af-card-gallery]');
      const images = gallery ? [...gallery.querySelectorAll('img')] : [];
      if (images.length < 2) return;
      const dots = [...card.querySelectorAll('[data-af-card-index]')];
      card.querySelector('.af-art-card__dots')?.removeAttribute('hidden');
      card.querySelector('.af-art-card__gallery-arrows')?.removeAttribute('hidden');
      let index = 0;
      let pointerStart = null;
      let dragged = false;
      const update = () => {
        const width = gallery.clientWidth || 1;
        index = Math.max(0, Math.min(images.length - 1, Math.round(gallery.scrollLeft / width)));
        dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === index)));
      };
      const show = (next) => {
        index = (next + images.length) % images.length;
        gallery.scrollTo({ left: index * gallery.clientWidth, behavior: scrollBehavior() });
        dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === index)));
      };
      dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.afCardIndex))));
      card.querySelector('[data-af-card-previous]')?.addEventListener('click', () => show(index - 1));
      card.querySelector('[data-af-card-next]')?.addEventListener('click', () => show(index + 1));
      gallery.addEventListener('scroll', update, { passive: true });
      gallery.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        pointerStart = { x: event.clientX, y: event.clientY };
        dragged = false;
      });
      gallery.addEventListener('pointermove', (event) => {
        if (pointerStart && Math.abs(event.clientX - pointerStart.x) > 10) dragged = true;
      });
      gallery.addEventListener('pointerup', (event) => {
        if (!pointerStart) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        if (event.pointerType === 'mouse' && Math.abs(deltaX) > 35 && Math.abs(deltaX) > Math.abs(deltaY)) show(index + (deltaX < 0 ? 1 : -1));
        pointerStart = null;
      });
      gallery.addEventListener('pointercancel', () => { pointerStart = null; });
      gallery.addEventListener('dragstart', (event) => event.preventDefault());
      gallery.addEventListener('click', (event) => { if (dragged) { event.preventDefault(); dragged = false; } });
      // A second product view follows hover; manual/touch controls retain every view.
      if (window.matchMedia('(hover: hover)').matches) {
        card.addEventListener('pointerenter', () => { if (index === 0) show(1); });
        card.addEventListener('pointerleave', () => { if (index === 1) show(0); });
      }
    });
  }

  class AfProductExperience extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      try { this.variants = JSON.parse(this.querySelector('[data-af-variant-data]')?.textContent || '[]'); }
      catch { this.variants = []; }
      this.native = this.querySelector('[data-af-variant-native]');
      this.form = this.querySelector('form');
      this.gallery = this.querySelector('[data-af-gallery]');
      this.media = [...this.querySelectorAll('[data-af-media-id]')];
      this.galleryIndex = 0;
      if (this.variants.length && this.native) {
        this.querySelector('[data-af-options]')?.removeAttribute('hidden');
        const fallback = this.querySelector('[data-af-variant-fallback]');
        if (fallback) fallback.hidden = true;
        this.querySelectorAll('[data-af-option-position] input').forEach((input) => input.addEventListener('change', () => this.chooseVariant(), options));
        this.native.addEventListener('change', () => this.renderVariant(this.variants.find((variant) => String(variant.id) === this.native.value), false), options);
        this.renderVariant(this.variants.find((variant) => String(variant.id) === this.native.value), false);
      }
      this.querySelectorAll('[data-af-quantity-change]').forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => {
          const quantity = this.form?.querySelector('[name="quantity"]');
          if (!quantity) return;
          if (Number(button.dataset.afQuantityChange) > 0) quantity.stepUp(); else quantity.stepDown();
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }, options);
      });
      const controls = this.querySelector('[data-af-gallery-controls]');
      if (controls && this.media.length > 1) controls.hidden = false;
      this.querySelector('[data-af-gallery-previous]')?.addEventListener('click', () => this.showMedia(this.galleryIndex - 1), options);
      this.querySelector('[data-af-gallery-next]')?.addEventListener('click', () => this.showMedia(this.galleryIndex + 1), options);
      this.gallery?.addEventListener('scroll', () => {
        const left = this.gallery.getBoundingClientRect().left;
        this.galleryIndex = this.media.reduce((best, item, index) => Math.abs(item.getBoundingClientRect().left - left) < Math.abs(this.media[best].getBoundingClientRect().left - left) ? index : best, 0);
        this.updateGalleryStatus();
      }, { passive: true, ...options });
      this.querySelectorAll('[data-af-zoom]').forEach((link) => link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        openZoom(this, link);
      }, options));
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    chooseVariant() {
      const selected = [...this.querySelectorAll('[data-af-option-position]')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
      const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
      this.renderVariant(variant);
      // Shopify's payment controls observe the actual variant field.
      if (variant && this.native) this.native.dispatchEvent(new Event('change', { bubbles: true }));
    }
    renderVariant(variant, announce = true) {
      const button = this.querySelector('[data-af-add-to-cart]');
      const label = button?.querySelector('[data-af-add-label]');
      const price = this.querySelector('[data-af-product-price]');
      const compare = this.querySelector('[data-af-product-compare]');
      const status = this.querySelector('[data-af-variant-status]');
      const quantity = this.form?.querySelector('[name="quantity"]');
      const available = Boolean(variant?.available);
      if (this.native) this.native.value = variant ? String(variant.id) : '';
      if (button) { button.disabled = !available; button.setAttribute('aria-disabled', String(!available)); }
      if (label) { label.textContent = !variant ? 'Unavailable' : available ? 'Add to cart' : 'Sold out'; label.classList.remove('hidden'); }
      this.querySelector('.sold-out-message')?.classList.add('hidden');
      this.querySelector('[data-af-dynamic-checkout]')?.toggleAttribute('hidden', !available);
      this.querySelectorAll('[data-af-option-position]').forEach((fieldset) => {
        const chosen = fieldset.querySelector('input:checked')?.value || '';
        const name = fieldset.querySelector('[data-af-option-value]');
        if (name) name.textContent = chosen;
      });
      if (price) price.textContent = variant?.price || '—';
      if (compare) { compare.hidden = !variant?.comparePrice; compare.textContent = variant?.comparePrice || ''; }
      if (variant && quantity) {
        const rule = variant.quantityRule || {};
        quantity.min = String(rule.min || 1);
        quantity.step = String(rule.increment || 1);
        if (rule.max) quantity.max = String(rule.max); else quantity.removeAttribute('max');
        let nextQuantity = Math.max(Number(quantity.min), Number(quantity.value) || 1);
        nextQuantity = Number(quantity.min) + Math.ceil((nextQuantity - Number(quantity.min)) / Number(quantity.step)) * Number(quantity.step);
        if (rule.max) nextQuantity = Math.min(nextQuantity, rule.max);
        quantity.value = String(nextQuantity);
      }
      if (!announce) return;
      if (status) status.textContent = !variant ? 'This combination is unavailable.' : `${variant.options.join(' / ')}. ${variant.price}. ${available ? 'Available.' : 'Sold out.'}`;
      if (variant && !this.closest('af-quick-view')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
      if (variant?.mediaId) {
        const mediaIndex = this.media.findIndex((item) => item.dataset.afMediaId === String(variant.mediaId));
        if (mediaIndex >= 0) this.showMedia(mediaIndex, true);
      }
    }
    showMedia(next, variantChange = false) {
      if (!this.media.length) return;
      this.galleryIndex = (next + this.media.length) % this.media.length;
      const target = this.media[this.galleryIndex];
      if (mobile.matches || this.closest('af-quick-view')) {
        this.gallery.scrollTo({ left: target.offsetLeft - this.media[0].offsetLeft, behavior: scrollBehavior() });
      } else if (variantChange) {
        target.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
      }
      this.updateGalleryStatus();
    }
    updateGalleryStatus() {
      const status = this.querySelector('[data-af-gallery-status]');
      if (status) status.textContent = `${this.galleryIndex + 1} / ${this.media.length}`;
    }
  }

  class AfBrowseResults extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.classList.add('is-enhanced');
      this.panel = this.querySelector('[data-af-filter-panel]');
      this.panelContent = this.panel?.querySelector('.af-filter-panel__content');
      if (mobile.matches) this.querySelectorAll('.af-filter-group').forEach((group) => { group.open = false; });
      this.querySelectorAll('[data-af-filter-close], [data-af-filter-apply], .af-grid-switch').forEach((item) => { item.hidden = false; });
      this.resetPending();
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-af-sort-form], [data-af-search-form], [data-af-mobile-sort], [data-af-native-filter-form]');
        if (!form) return;
        event.preventDefault();
        if (form.matches('[data-af-mobile-sort]')) { this.applyFilters(); return; }
        if (!form.reportValidity()) return;
        const url = preservePreview(form.action);
        if (form.matches('[data-af-sort-form]')) {
          new URL(window.location.href).searchParams.forEach((value, key) => { if (key.startsWith('filter.')) url.searchParams.append(key, value); });
        }
        new FormData(form).forEach((value, key) => { if (String(value).trim()) url.searchParams.append(key, value); });
        this.navigate(url);
      }, options);
      this.querySelector('[data-af-sort-form] select')?.addEventListener('change', (event) => event.target.form.requestSubmit(), options);
      this.panel?.addEventListener('toggle', () => this.panelState(), options);
      this.addEventListener('keydown', (event) => {
        if (!this.panel?.open) return;
        if (event.key === 'Escape') { event.preventDefault(); this.closeFilters(); }
        if (mobile.matches && this.panelContent) trapTab(event, this.panelContent);
      }, options);
      window.addEventListener('popstate', () => this.navigate(window.location.href, false), options);
      mobile.addEventListener('change', () => {
        if (mobile.matches) this.querySelectorAll('.af-filter-group').forEach((group) => { group.open = false; });
        this.panelState();
      }, options);
      initializeCards(this);
      const columns = this.querySelector('[data-af-results-grid]')?.style.getPropertyValue('--af-columns-desktop')?.trim() || '3';
      this.setGrid(columns);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.fetchController?.abort();
      this.restoreIsolation?.(); this.restoreIsolation = null;
      document.body.classList.remove('af-filter-open');
    }
    resetPending() {
      try { this.pendingTags = new Set(JSON.parse(this.dataset.currentTags || '[]')); }
      catch { this.pendingTags = new Set(); }
    }
    panelState() {
      const open = Boolean(this.panel?.open && mobile.matches);
      document.body.classList.toggle('af-filter-open', open);
      if (this.panelContent) {
        if (open) {
          this.panelContent.setAttribute('role', 'dialog'); this.panelContent.setAttribute('aria-modal', 'true'); this.panelContent.setAttribute('aria-label', 'Filter and sort');
          if (!this.restoreIsolation) this.restoreIsolation = isolateOverlay(this.panelContent);
          this.panelContent.querySelector('h2')?.focus();
        } else {
          this.panelContent.removeAttribute('role'); this.panelContent.removeAttribute('aria-modal');
          this.restoreIsolation?.(); this.restoreIsolation = null;
        }
      }
    }
    closeFilters() {
      if (this.panel) this.panel.open = false;
      this.restoreIsolation?.(); this.restoreIsolation = null;
      this.panel?.querySelector('summary')?.focus();
      document.body.classList.remove('af-filter-open');
    }
    setGrid(columns) {
      const grid = this.querySelector('[data-af-results-grid]');
      if (grid) grid.style.setProperty('--af-columns-desktop', columns);
      this.querySelectorAll('[data-af-grid]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.afGrid === columns)));
    }
    onClick(event) {
      const gridButton = event.target.closest('[data-af-grid]');
      if (gridButton) { this.setGrid(gridButton.dataset.afGrid); return; }
      if (event.target.closest('[data-af-filter-close]')) { this.closeFilters(); return; }
      if (event.target.closest('[data-af-filter-apply]')) { this.applyFilters(); return; }
      const link = event.target.closest('a[data-af-result-link]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      if (mobile.matches && this.panel?.open && this.panel.contains(link)) {
        const filter = link.closest('[data-af-filter-tag]');
        if (filter) {
          const tag = filter.dataset.afFilterTag;
          if (this.pendingTags.has(tag)) this.pendingTags.delete(tag); else this.pendingTags.add(tag);
          link.toggleAttribute('aria-current', this.pendingTags.has(tag));
          if (this.pendingTags.has(tag)) link.setAttribute('aria-current', 'true');
        } else if (link.hasAttribute('data-af-filter-clear')) {
          this.pendingTags.clear();
          this.panel.querySelectorAll('[data-af-filter-tag] a').forEach((item) => item.removeAttribute('aria-current'));
          const sort = this.querySelector('[data-af-mobile-sort] select');
          if (sort) sort.selectedIndex = 0;
          this.querySelectorAll('[data-af-native-filter-form] input:not([type="hidden"])').forEach((input) => { if (input.type === 'checkbox') input.checked = false; else input.value = ''; });
        }
        return;
      }
      const url = preservePreview(link.href);
      const current = new URL(window.location.href);
      if (!url.searchParams.has('sort_by') && current.searchParams.has('sort_by')) url.searchParams.set('sort_by', current.searchParams.get('sort_by'));
      if (!link.hasAttribute('data-af-clear-all')) [...new Set(current.searchParams.keys())].forEach((key) => {
        if (key.startsWith('filter.') && !url.searchParams.has(key)) current.searchParams.getAll(key).forEach((value) => url.searchParams.append(key, value));
      });
      this.navigate(url);
    }
    applyFilters() {
      const base = this.dataset.collectionUrl;
      if (!base) return;
      const path = base + (this.pendingTags.size ? '/' + [...this.pendingTags].map(encodeURIComponent).join('+') : '');
      const url = preservePreview(path);
      const nativeForm = this.querySelector('[data-af-native-filter-form]');
      if (nativeForm) {
        if (!nativeForm.reportValidity()) return;
        new FormData(nativeForm).forEach((value, key) => { if (key.startsWith('filter.') && String(value).trim()) url.searchParams.append(key, value); });
      }
      const sort = this.querySelector('[data-af-mobile-sort] select');
      if (sort?.value) url.searchParams.set('sort_by', sort.value);
      this.closeFilters();
      this.navigate(url);
    }
    async navigate(value, pushHistory = true) {
      const url = preservePreview(value);
      if (url.origin !== window.location.origin) { window.location.assign(url); return; }
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      const signal = this.fetchController.signal;
      const request = new URL(url);
      request.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-af-results-status]');
      if (status) status.textContent = 'Loading works…';
      const columns = this.querySelector('[data-af-results-grid]')?.style.getPropertyValue('--af-columns-desktop');
      try {
        const response = await fetch(request, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error('Unable to load works');
        const html = await response.text();
        if (signal.aborted) return;
        const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-af-browse]');
        if (!incoming) throw new Error('Collection response unavailable');
        const next = document.importNode(incoming, true);
        if (pushHistory) window.history.pushState({ afBrowse: true }, '', url);
        this.replaceWith(next);
        if (columns) next.setGrid?.(columns.trim());
        const heading = next.querySelector('[data-af-browse-tools]') || next;
        heading.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
        const focusTarget = next.querySelector('.af-collection__title, .af-search__title');
        focusTarget?.focus({ preventScroll: true });
        document.title = `${focusTarget?.textContent?.trim() || 'Collection'} — Alex Frishberg Art`;
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(url);
      } finally { if (this.isConnected) this.removeAttribute('aria-busy'); }
    }
  }

  let productFormPromise;
  function ensureProductForm(source) {
    if (customElements.get('product-form')) return Promise.resolve();
    if (productFormPromise) return productFormPromise;
    productFormPromise = new Promise((resolve, reject) => {
      if (!source) { reject(new Error('Product form unavailable')); return; }
      const script = document.createElement('script');
      script.src = source;
      script.onload = () => customElements.get('product-form') ? resolve() : reject(new Error('Product form unavailable'));
      script.onerror = () => { productFormPromise = null; reject(new Error('Product form could not load')); };
      document.head.append(script);
    });
    return productFormPromise;
  }

  class AfQuickView extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.innerHTML = '<div class="af-quick-view__scrim" data-af-quick-close></div><div class="af-quick-view__panel" role="dialog" aria-modal="true" aria-label="Quick view" tabindex="-1"><h2 class="af-quick-view__heading">Quick view</h2><button class="af-quick-view__close" type="button" data-af-quick-close>Close ×</button><div class="af-quick-view__content"></div></div>';
      this.panel = this.querySelector('.af-quick-view__panel');
      this.content = this.querySelector('.af-quick-view__content');
      this.addEventListener('click', (event) => { if (event.target.closest('[data-af-quick-close]')) this.close(); });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(); }
        trapTab(event, this.panel);
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!this.hidden && event.source === 'product-form') this.close(false);
        });
      }
    }
    disconnectedCallback() { this.unsubscribe?.(); this.request?.abort(); this.restoreIsolation?.(); }
    async open(trigger) {
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      this.trigger = trigger;
      this.hidden = false;
      document.body.classList.add('af-quick-view-open');
      if (!this.restoreIsolation) this.restoreIsolation = isolateOverlay(this);
      this.content.innerHTML = '<p class="af-quick-view__loading" role="status">Loading artwork…</p>';
      this.panel.focus();
      const url = preservePreview(trigger.dataset.afQuickView);
      try {
        const [response] = await Promise.all([fetch(url, { credentials: 'same-origin', signal }), ensureProductForm(trigger.dataset.afProductFormSrc)]);
        if (!response.ok) throw new Error('Product unavailable');
        const html = await response.text();
        if (signal.aborted) return;
        const core = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-af-product-core]');
        if (!core) throw new Error('This work is not available in quick view.');
        core.querySelectorAll('.af-product__breadcrumbs').forEach((item) => item.remove());
        if (!window.Shopify?.PaymentButton?.init) core.querySelector('[data-af-dynamic-checkout]')?.remove();
        core.querySelector('[data-af-full-details]')?.removeAttribute('hidden');
        // Prefix IDs before importing the complete subtree so native forms and labels remain paired.
        const idMap = new Map();
        core.querySelectorAll('[id]').forEach((item) => { const old = item.id; item.id = `Quick-${old}`; idMap.set(old, item.id); });
        core.querySelectorAll('[for], [aria-labelledby], [aria-describedby], [aria-controls], [form]').forEach((item) => {
          ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'form'].forEach((attribute) => {
            if (!item.hasAttribute(attribute)) return;
            item.setAttribute(attribute, item.getAttribute(attribute).split(' ').map((id) => idMap.get(id) || id).join(' '));
          });
        });
        const title = core.querySelector('h1');
        if (title) this.panel.setAttribute('aria-labelledby', title.id);
        this.content.replaceChildren(document.importNode(core, true));
        window.Shopify?.PaymentButton?.init?.();
        this.querySelector('.af-quick-view__close').focus();
      } catch (error) {
        if (error.name === 'AbortError') return;
        const message = document.createElement('p');
        message.setAttribute('role', 'status');
        message.textContent = 'Quick view could not load.';
        const link = document.createElement('a');
        link.href = url;
        link.textContent = 'View the product ↗';
        this.content.replaceChildren(message, link);
      }
    }
    close(restore = true) {
      this.request?.abort();
      this.hidden = true;
      document.body.classList.remove('af-quick-view-open');
      this.restoreIsolation?.(); this.restoreIsolation = null;
      this.panel.removeAttribute('aria-labelledby');
      if (!restore) document.querySelector('cart-drawer')?.setActiveElement?.(this.trigger);
      if (restore) this.trigger?.focus();
    }
  }

  function openZoom(experience, trigger) {
    const dialog = document.createElement('dialog');
    dialog.className = 'af-media-zoom';
    dialog.setAttribute('aria-label', 'Enlarged product images');
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'af-media-zoom__close'; close.textContent = 'Close ×';
    const rail = document.createElement('div'); rail.className = 'af-media-zoom__images';
    let currentImage;
    experience.querySelectorAll('[data-af-zoom]').forEach((link) => {
      const img = document.createElement('img');
      img.src = link.href; img.alt = link.dataset.afZoomAlt || ''; img.loading = 'lazy';
      const source = link.querySelector('img');
      if (source?.width && source?.height) { img.width = source.width; img.height = source.height; }
      rail.append(img);
      if (link === trigger) currentImage = img;
    });
    dialog.append(close, rail); document.body.append(dialog);
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => { dialog.remove(); trigger.focus(); document.body.classList.remove('af-media-zoom-open'); });
    dialog.showModal(); document.body.classList.add('af-media-zoom-open');
    currentImage?.scrollIntoView({ block: 'start' }); close.focus({ preventScroll: true });
  }

  if (!customElements.get('af-product-experience')) customElements.define('af-product-experience', AfProductExperience);
  if (!customElements.get('af-browse-results')) customElements.define('af-browse-results', AfBrowseResults);
  if (!customElements.get('af-quick-view')) customElements.define('af-quick-view', AfQuickView);
  const initialize = (scope = document) => {
    initializeCards(scope);
    scope.querySelectorAll('[data-af-quick-view]').forEach((trigger) => { trigger.hidden = false; });
    if (scope.matches?.('[data-af-quick-view]')) scope.hidden = false;
  };
  const start = () => {
    initialize();
    new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
    }))).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-af-quick-view]');
      if (!trigger) return;
      let dialog = document.querySelector('af-quick-view');
      if (!dialog) { dialog = document.createElement('af-quick-view'); document.body.append(dialog); }
      dialog.open(trigger);
    });
  };
  window.AFWrongCommerce = { refresh: initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
